import { Injectable, Injector } from '@angular/core';
import { Location } from '@angular/common';
import {
  BookingEventSetupState,
  BookingEventSetupStatus,
} from '@deltager/organizers/admin/modules/booking-event-setup/store/booking-event-setup.state';
import { UserService } from '@deltager/organizers/admin/core/services/user.service';
import { catchError, concatMap, finalize, mergeMap, switchMap, take } from 'rxjs/operators';
import { UserData } from '@deltager/organizers/admin/shared/models/LoginData';
import { BookingEvent, buildBookingEvent } from '@deltager/organizers/admin/shared/models/booking-event.model';
import { BookingEventService } from '@deltager/organizers/admin/modules/booking-event-setup/shared/booking-event.service';
import { BookingShowNotificationAction } from '@deltager/organizers/admin/modules/booking-event-setup/store/booking-event-setup.ui.actions';
import { throwError, timer, of, merge } from 'rxjs';
import { AppNotificationType } from '@deltager/organizers/admin/shared/models/AppNotificationType';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  AdjustDocumentPropertiesAction,
  BookingEventActionTypes,
  BookingEventStatusChangeAction,
  ConstructBookingEventRequestAction,
  ConstructBookingEventSuccessAction,
  CreateBookingEventRequestAction,
  CreateBookingEventSuccessAction,
  LoadBookingEventRequestAction,
  LoadBookingEventSuccessAction,
  PublishBookingEventRequestAction,
  PublishBookingEventSuccessAction,
  GeneralUpdateBookingEventErrorAction,
  UpdateBookingEventRequestAction,
  UpdateBookingEventSuccessAction,
} from './booking-event-setup.actions';
import { SpinnerOverlayService } from '@deltager/organizers/admin/shared/spinner-overlay/spinner-overlay.service';
import _ from 'lodash';
import { StorageService } from '@deltager/organizers/admin/core/services/storage.service';
import { AppNotificationsService } from '@deltager/organizers/admin/core/services/app-notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { PaymentServiceProviderService } from '../../payment-service-provider/payment-service-provider.service';
import { SaveChangesSource, selectOrganizer } from '@deltager/organizers/admin/redux/root.state';
import { BaseBookingApiService } from '@deltager/organizers/admin/shared/services/base-booking.api.service';
import { EventType } from '@deltager/organizers/admin/shared/models/EventType';
import { isNullOrUndefined } from '@deltager/organizers/admin/shared/utils/object.utils';

@Injectable({ providedIn: 'root' })
export class BookingEventSetupEffects {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private baseBookingApiService: BaseBookingApiService,
    private bookingEventService: BookingEventService,
    private store: Store<{ booking: BookingEventSetupState }>,
    private spinner: SpinnerOverlayService,
    private storageService: StorageService,
    private notificationService: AppNotificationsService,
    private injector: Injector,
    private location: Location,
    private router: Router,
    private paymentServiceProviderService: PaymentServiceProviderService,
  ) {}

  constructEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingEventActionTypes.ConstructBookingEventRequest),
      mergeMap((action: ConstructBookingEventRequestAction) => {
        this.store.dispatch(
          new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Initializing }),
        );
        if (action.payload.preferCached) {
          const storedData = this.storageService.getItemFromSession<BookingEvent>('cachedInitialBookingEvent', false);
          if (storedData) {
            return [
              new ConstructBookingEventSuccessAction({ bookingEvent: storedData }),
              new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Initialized }),
            ];
          }
        }
        return this.store.select(selectOrganizer).pipe(
          take(1),
          switchMap(organizer => {
            const userData = this.userService.getUserDataInstant() as UserData;
            const bookingEvent = buildBookingEvent(
              userData.user.fullName,
              userData.user.email,
              userData.user.mobile,
              organizer.orgNumber,
              organizer.personNumber,
            );

            return [
              new ConstructBookingEventSuccessAction({ bookingEvent: bookingEvent }),
              new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Initialized }),
            ];
          }),
        );
      }),
    );
  });

  createBookingEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingEventActionTypes.CreateBookingEventRequest),
      mergeMap((action: CreateBookingEventRequestAction) => {
        this.spinner.show();

        const bookingEvent = action.payload.bookingEvent;

        return this.bookingEventService.create(bookingEvent, action.payload.wizardId ?? 0).pipe(
          switchMap(newBookingEventId => this.baseBookingApiService.getById(newBookingEventId)),
          switchMap((be: BookingEvent) => {
            this.spinner.hide();

            const todoActions: Action[] = [
              new CreateBookingEventSuccessAction({ bookingEvent: be }),
              new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Created }),
            ];

            if (be.documentUrls?.length) {
              todoActions.push(
                new AdjustDocumentPropertiesAction({
                  documentUrls: be.documentUrls,
                }),
              );
            }

            if (action.payload.notificationMessage) {
              todoActions.push(
                new BookingShowNotificationAction({
                  message: action.payload.notificationMessage,
                  notificationType: AppNotificationType.SUCCESS,
                }),
              );
            }

            this.location.replaceState(`/booking-events/${be.id}`);

            return todoActions;
          }),
          catchError(err =>
            merge(
              of(new GeneralUpdateBookingEventErrorAction()),
              throwError(() => err),
            ),
          ),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });

  private onboardReturnUrl(eventId: number): string {
    let url = window.location.href;
    const split = url.split('#');
    return `${split[0]}#/post-onboarding?eventId=${eventId}&eventType=${EventType.BookingV2}`;
  }

  updateBookingEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingEventActionTypes.UpdateBookingEventRequest),
      mergeMap((action: UpdateBookingEventRequestAction) => {
        this.spinner.show();

        const bookingEventId = action.payload.bookingEventId;
        const updateOperations = _.cloneDeep(action.payload.updateOperations);

        return this.bookingEventService.update(bookingEventId, updateOperations, action.payload.draftMode).pipe(
          switchMap(_ => this.baseBookingApiService.getById(bookingEventId)),
          switchMap((be: BookingEvent) => {
            if (action.payload.source === SaveChangesSource.onboard) {
              const url = this.onboardReturnUrl(be.id);
              this.paymentServiceProviderService.onboard(be.id, EventType.BookingV2, url).then();
            }

            return of(be);
          }),
          switchMap((be: BookingEvent) => {
            const todoActions: Action[] = [
              new UpdateBookingEventSuccessAction({ bookingEvent: be }),
              new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Updated }),
            ];

            if (action.payload.notificationMessage) {
              todoActions.push(
                new BookingShowNotificationAction({
                  message: action.payload.notificationMessage,
                  notificationType: AppNotificationType.SUCCESS,
                }),
              );
            }

            return todoActions;
          }),
          catchError(err =>
            merge(
              of(new GeneralUpdateBookingEventErrorAction()),
              throwError(() => err),
            ),
          ),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });

  loadBookingEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingEventActionTypes.LoadBookingEventRequest),
      mergeMap((action: LoadBookingEventRequestAction) => {
        this.store.dispatch(
          new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Initializing }),
        );
        return this.baseBookingApiService.getById(action.payload.bookingEventId).pipe(
          switchMap((be: BookingEvent) => {
            this.adjustInitial(be);

            return [
              new LoadBookingEventSuccessAction({ bookingEvent: be }),
              new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Initialized }),
            ];
          }),
          catchError((err: HttpErrorResponse) => {
            if (err.status === HttpStatusCode.NotFound) {
              const translateService = this.injector.get(TranslateService);

              this.notificationService.sendError({
                message: translateService.instant('validation.cant_find_booking_event'),
              });
              return timer(3000).pipe(
                switchMap(() => {
                  this.router.navigate(['page-not-found'], { skipLocationChange: true });
                  return throwError(() => err);
                }),
              );
            }
            return throwError(() => err);
          }),
        );
      }),
    );
  });

  publishBookingEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingEventActionTypes.PublishBookingEventRequest),
      mergeMap((action: PublishBookingEventRequestAction) => {
        this.spinner.show();

        const bookingEventId = action.payload.bookingEventId;
        const updateOperations = _.cloneDeep(action.payload.updateOperations);

        return this.bookingEventService.update(bookingEventId, updateOperations).pipe(
          concatMap(_ => this.bookingEventService.publish(bookingEventId)),
          concatMap(_ => this.baseBookingApiService.getById(bookingEventId)),
          switchMap((be: BookingEvent) => {
            this.spinner.hide();

            return [
              new BookingShowNotificationAction({
                message: action.payload.notificationMessage,
                notificationType: AppNotificationType.SUCCESS,
              }),
              new PublishBookingEventSuccessAction({ bookingEvent: be }),
              new BookingEventStatusChangeAction({ bookingEventStatus: BookingEventSetupStatus.Published }),
            ];
          }),
          catchError(err =>
            merge(
              of(new GeneralUpdateBookingEventErrorAction()),
              throwError(() => err),
            ),
          ),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });

  private adjustInitial(bookingEvent: BookingEvent): void {
    bookingEvent.priceCategories.forEach(pc => {
      if (isNullOrUndefined(pc.userAssetRegistration)) {
        delete pc.userAssetRegistration;
      }
    });
  }
}
